import { apiClient, handleApiError, isBackendEnabled } from '../client';
import {
    ApiResponse,
    ShoppingListResponse,
    ShoppingListsResponse,
    CreateShoppingListRequest,
    UpdateShoppingListRequest,
    BackendShoppingList,
    ShoppingListsArray,
    BackendShoppingListItem,
    ShoppingListItemsArray,
    ShoppingListItemsResponse,
    AddShoppingListItemRequest,
    CalculateRouteResponse,
    ProductLocationResponse,
} from '../types';
import { SavedShoppingList } from '../../redux/shoppingListSlice';
import { mockShoppingListApi } from '../mock';

/**
 * Shopping List API Service
 * Handles CRUD operations for shopping lists
 * Uses backend API if enabled, otherwise uses localStorage
 */
// Transform backend shopping list items (legacy array) to items record
const transformShoppingListItems = (items: BackendShoppingListItem[]): Record<string, number> => {
    const itemsRecord: Record<string, number> = {};
    items.forEach((item) => {
        itemsRecord[item.product_id] = (itemsRecord[item.product_id] || 0) + 1;
    });
    return itemsRecord;
};

// Transform GET /shopping-list/{id}/items response (products payload) to items record
const transformProductsToItemsRecord = (payload: ShoppingListItemsResponse): Record<string, number> => {
    const itemsRecord: Record<string, number> = {};
    (payload.products || []).forEach((p) => {
        itemsRecord[p.product_id] = p.quantity;
    });
    return itemsRecord;
};

// Transform backend shopping list to SavedShoppingList format
// is_active from GET /shopping-list/user/{userId} is used for the list switch
const transformBackendShoppingList = (
    backendList: BackendShoppingList,
    items?: Record<string, number>
): SavedShoppingList => {
    return {
        id: backendList.id.toString(),
        name: backendList.list_name,
        items: items || {},
        createdAt: backendList.created_at,
        updatedAt: backendList.updated_at || backendList.created_at, // Use created_at as fallback if updated_at is null
        active: backendList.is_active ?? false,
        is_completed: backendList.is_completed ?? false,
    };
};

// Simple session id helper for audit events.
const getAuditSessionId = (): string => {
    try {
        const existing = localStorage.getItem('shoppingAuditSessionId');
        if (existing) return existing;
        const generated = `session-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
        localStorage.setItem('shoppingAuditSessionId', generated);
        return generated;
    } catch {
        return 'anonymous';
    }
};

export const shoppingListApi = {
    /**
     * Get all shopping lists for the current user
     * GET /shopping-list/user/{userId}?skip=0&limit=1000
     * Response includes is_active per list (used for the active/inactive switch).
     */
    getShoppingLists: async (skip: number = 0, limit: number = 1000): Promise<ShoppingListsResponse> => {
        if (!isBackendEnabled() || !apiClient) {
            return mockShoppingListApi.getShoppingLists();
        }

        try {
            // Get userId from localStorage
            const userId = localStorage.getItem('userId');
            if (!userId) {
                throw new Error('User ID not found. Please select a persona first.');
            }

            // Backend returns array of lists with list_name, is_active, etc.; items fetched separately
            const response = await apiClient.get<ShoppingListsArray>(`/shopping-list/user/${userId}`, {
                params: {
                    skip,
                    limit,
                },
            });
            const backendLists = response.data;
            // Transform backend format to SavedShoppingList (is_active -> active for switch)
            const lists = backendLists.map((backendList) => transformBackendShoppingList(backendList));
            return {
                lists,
                total: lists.length,
            };
        } catch (error) {
            throw handleApiError(error);
        }
    },

    /**
     * Get shopping list items by list ID
     * GET /shopping-list/{id}/items returns { shopping_list_id, products: [{ product_id, quantity, item_ids, ... }] }
     */
    getShoppingListItems: async (listId: string | number): Promise<Record<string, number>> => {
        if (!isBackendEnabled() || !apiClient) {
            const mockList = await mockShoppingListApi.getShoppingListById(listId.toString());
            return mockList.list.items;
        }

        try {
            const response = await apiClient.get<ShoppingListItemsResponse | ShoppingListItemsArray>(
                `/shopping-list/${listId}/items`
            );
            const data = response.data;
            if (data && typeof data === 'object' && 'products' in data) {
                return transformProductsToItemsRecord(data as ShoppingListItemsResponse);
            }
            return transformShoppingListItems((data as ShoppingListItemsArray) || []);
        } catch (error) {
            throw handleApiError(error);
        }
    },

    /**
     * Get shopping list items with full payload (including item_ids per product).
     * Used when reducing quantity so we can DELETE by item_id.
     */
    getShoppingListItemsDetailed: async (listId: string | number): Promise<ShoppingListItemsResponse | null> => {
        if (!isBackendEnabled() || !apiClient) {
            return null;
        }
        try {
            const response = await apiClient.get<ShoppingListItemsResponse | ShoppingListItemsArray>(
                `/shopping-list/${listId}/items`
            );
            const data = response.data;
            if (data && typeof data === 'object' && 'products' in data) {
                return data as ShoppingListItemsResponse;
            }
            return null;
        } catch (error) {
            return null;
        }
    },

    /**
     * Add an item to a shopping list
     * POST /shopping-list/{shoppingListId}/items
     * Body: { product_id: string, quantity: number }
     */
    addShoppingListItem: async (
        shoppingListId: string | number,
        itemId: string,
        payload: AddShoppingListItemRequest
    ): Promise<void> => {
        if (!isBackendEnabled() || !apiClient) {
            return;
        }

        try {
            const product_id = payload.product_id ?? itemId;
            const quantity = payload.quantity ?? 1;
            await apiClient.post(`/shopping-list/${shoppingListId}/items`, {
                product_id,
                quantity,
            });
        } catch (error) {
            throw handleApiError(error);
        }
    },

    /**
     * Delete all items from a shopping list
     * DELETE /shopping-list/{listId}/items
     */
    deleteAllShoppingListItems: async (listId: string | number): Promise<void> => {
        if (!isBackendEnabled() || !apiClient) {
            return;
        }
        try {
            await apiClient.delete(`/shopping-list/${listId}/items`);
        } catch (error) {
            throw handleApiError(error);
        }
    },

    /**
     * Delete all instances of a product from a shopping list (e.g. when user clicks trash).
     * DELETE /shopping-list/{id}/items/product/{product_id}
     */
    deleteShoppingListItem: async (
        shoppingListId: string | number,
        productId: string
    ): Promise<void> => {
        if (!isBackendEnabled() || !apiClient) {
            return;
        }
        try {
            await apiClient.delete(`/shopping-list/${shoppingListId}/items/product/${productId}`);
        } catch (error) {
            throw handleApiError(error);
        }
    },

    /**
     * Delete a single item by row id (e.g. when reducing quantity).
     * DELETE /shopping-list/{id}/items/{item_id}
     */
    deleteShoppingListItemByItemId: async (
        shoppingListId: string | number,
        itemId: number
    ): Promise<void> => {
        if (!isBackendEnabled() || !apiClient) {
            return;
        }
        try {
            await apiClient.delete(`/shopping-list/${shoppingListId}/items/${itemId}`);
        } catch (error) {
            throw handleApiError(error);
        }
    },

    /**
     * Get a single shopping list by ID (with items)
     */
    getShoppingListById: async (listId: string | number, includeItems: boolean = true): Promise<ShoppingListResponse> => {
        if (!isBackendEnabled() || !apiClient) {
            return mockShoppingListApi.getShoppingListById(listId.toString());
        }

        try {
            // Try to get single shopping list endpoint first, fallback to filtering from all lists
            let backendList: BackendShoppingList;
            try {
                const response = await apiClient.get<BackendShoppingList>(`/shopping-list/${listId}`);
                backendList = response.data;
            } catch {
                // Fallback: get all lists for user and filter by id
                const userId = localStorage.getItem('userId');
                if (!userId) {
                    throw new Error('User ID not found. Please select a persona first.');
                }
                const response = await apiClient.get<ShoppingListsArray>(`/shopping-list/user/${userId}`, {
                    params: { skip: 0, limit: 1000 },
                });
                const listIdNum = typeof listId === 'string' ? parseInt(listId, 10) : listId;
                const found = response.data.find((list) => list.id === listIdNum);
                if (!found) {
                    throw new Error(`Shopping list with ID ${listId} not found`);
                }
                backendList = found;
            }

            // Fetch items if requested
            let items: Record<string, number> = {};
            if (includeItems) {
                try {
                    items = await shoppingListApi.getShoppingListItems(listId);
                } catch (error) {
                    console.warn('Failed to fetch shopping list items:', error);
                    // Continue with empty items if fetch fails
                }
            }

            // Transform to SavedShoppingList format
            const list = transformBackendShoppingList(backendList, items);

            return { list };
        } catch (error) {
            throw handleApiError(error);
        }
    },

    /**
     * Create a new shopping list
     * Backend: POST /shopping-list with body { user_id, store_id, list_name }
     */
    createShoppingList: async (
        data: CreateShoppingListRequest
    ): Promise<ShoppingListResponse> => {
        if (!isBackendEnabled() || !apiClient) {
            if (process.env.NODE_ENV === 'development') {
                console.warn('[ShoppingList API] Create list: using mock (backend disabled). Set REACT_APP_USE_BACKEND=true and restart.');
            }
            return mockShoppingListApi.createShoppingList(data);
        }

        try {
            const userIdRaw = data.user_id !== undefined ? data.user_id : localStorage.getItem('userId');
            const storeIdRaw = data.store_id !== undefined ? data.store_id : localStorage.getItem('storeId');
            const user_id = typeof userIdRaw === 'number' ? userIdRaw : (userIdRaw != null ? parseInt(String(userIdRaw), 10) : 0);
            // Assume store_id 1 when not provided
            const store_id = typeof storeIdRaw === 'number' ? storeIdRaw : (storeIdRaw != null ? parseInt(String(storeIdRaw), 10) : 1);
            const storeId = Number.isNaN(store_id) ? 1 : store_id;

            const response = await apiClient.post<BackendShoppingList | ApiResponse<{ list: BackendShoppingList }>>(
                '/shopping-list',
                { user_id: Number.isNaN(user_id) ? 0 : user_id, store_id: storeId, list_name: data.name }
            );
            const raw = response.data as any;
            const backendList: BackendShoppingList = raw?.data?.list ?? raw;
            const listId = backendList.id.toString();
            const items = data.items ?? {};
            if (Object.keys(items).length > 0) {
                for (const [product_id, quantity] of Object.entries(items)) {
                    await apiClient.post(`/shopping-list/${listId}/items`, {
                        product_id,
                        quantity,
                    });
                }
            }
            const list = transformBackendShoppingList(backendList, items);
            return { list };
        } catch (error) {
            throw handleApiError(error);
        }
    },

    /**
     * Update an existing shopping list (name and/or active).
     * Name: PATCH /shopping-list/{id}/name with body { list_name: string }
     */
    updateShoppingList: async (
        listId: string,
        data: UpdateShoppingListRequest
    ): Promise<ShoppingListResponse> => {
        if (!isBackendEnabled() || !apiClient) {
            return mockShoppingListApi.updateShoppingList(listId, data);
        }

        try {
            if (data.name !== undefined) {
                await apiClient.patch(`/shopping-list/${listId}/name`, {
                    list_name: data.name,
                });
            }
            if (data.active !== undefined) {
                await apiClient.patch(`/shopping-list/${listId}/active`, {
                    is_active: data.active,
                });
            }
            const response = await shoppingListApi.getShoppingListById(listId, true);
            if (data.items !== undefined) {
                response.list.items = data.items;
            }
            return response;
        } catch (error) {
            throw handleApiError(error);
        }
    },

    /**
     * Save list when editing: clear all items then POST each (product_id, quantity).
     * Clear: DELETE /shopping-list/{id}/items. Then POST /shopping-list/{id}/items per item.
     */
    saveListForEdit: async (
        listId: string,
        listName: string,
        items: Record<string, number>
    ): Promise<ShoppingListResponse> => {
        if (!isBackendEnabled() || !apiClient) {
            return mockShoppingListApi.updateShoppingList(listId, { name: listName, items });
        }

        try {
            await shoppingListApi.deleteAllShoppingListItems(listId);
            for (const [product_id, quantity] of Object.entries(items)) {
                await apiClient.post(`/shopping-list/${listId}/items`, {
                    product_id,
                    quantity,
                });
            }
            return await shoppingListApi.getShoppingListById(listId, true);
        } catch (error) {
            throw handleApiError(error);
        }
    },

    /**
     * Delete a shopping list
     * DELETE /shopping-list/{shopping_list_id} (if backend supports)
     */
    deleteShoppingList: async (listId: string): Promise<void> => {
        if (!isBackendEnabled() || !apiClient) {
            return mockShoppingListApi.deleteShoppingList(listId);
        }

        try {
            await apiClient.delete(`/shopping-list/${listId}`);
        } catch (error) {
            throw handleApiError(error);
        }
    },

    /**
     * Set a shopping list as active or inactive
     * PATCH /shopping-list/{id}/active with body { is_active: boolean }
     */
    setActiveShoppingList: async (listId: string, active: boolean): Promise<ShoppingListResponse> => {
        if (!isBackendEnabled() || !apiClient) {
            return mockShoppingListApi.setActiveShoppingList(listId, active);
        }

        try {
            await apiClient.patch(`/shopping-list/${listId}/active`, {
                is_active: active,
            });
            return await shoppingListApi.getShoppingListById(listId, true);
        } catch (error) {
            throw handleApiError(error);
        }
    },

    /**
     * Add items to a shopping list
     */
    addItemsToList: async (
        listId: string | number,
        items: Record<string, number>
    ): Promise<ShoppingListResponse> => {
        if (!isBackendEnabled() || !apiClient) {
            return mockShoppingListApi.addItemsToList(listId.toString(), items);
        }

        try {
            const listIdNum = typeof listId === 'string' ? parseInt(listId, 10) : listId;

            for (const [productId, quantity] of Object.entries(items)) {
                try {
                    await apiClient.post(`/shopping-list/${listIdNum}/items`, {
                        product_id: productId,
                        quantity,
                    });
                } catch (error) {
                    console.warn(`Failed to add item ${productId}:`, error);
                }
            }

            return await shoppingListApi.getShoppingListById(listIdNum, true);
        } catch (error) {
            throw handleApiError(error);
        }
    },

    /**
     * Remove items from a shopping list
     * DELETE /shopping-list/{listId}/items/{productId} per product
     */
    removeItemsFromList: async (
        listId: string,
        productIds: string[]
    ): Promise<ShoppingListResponse> => {
        if (!isBackendEnabled() || !apiClient) {
            return mockShoppingListApi.removeItemsFromList(listId, productIds);
        }

        try {
            for (const productId of productIds) {
                await apiClient.delete(`/shopping-list/${listId}/items/${productId}`);
            }
            return await shoppingListApi.getShoppingListById(listId, true);
        } catch (error) {
            throw handleApiError(error);
        }
    },

    /**
     * Update item (e.g. scanned status)
     * Legacy quantity update:
     * PATCH /shopping-list/{shopping_list_id}/items/{product_id} with { quantity }
     */
    updateItemQuantity: async (
        listId: string,
        productId: string,
        quantity: number
    ): Promise<ShoppingListResponse> => {
        if (!isBackendEnabled() || !apiClient) {
            return mockShoppingListApi.updateItemQuantity(listId, productId, quantity);
        }

        try {
            await apiClient.patch(`/shopping-list/${listId}/items/${productId}`, { quantity });
            return await shoppingListApi.getShoppingListById(listId, true);
        } catch (error) {
            throw handleApiError(error);
        }
    },

    /**
     * Mark a specific shopping list item row as scanned/unscanned.
     * Backend: PATCH /shopping-list/{shopping_list_id}/items/{item_id} with { scanned: boolean }
     */
    setItemScanned: async (
        listId: string | number,
        itemId: number,
        scanned: boolean
    ): Promise<void> => {
        if (!isBackendEnabled() || !apiClient) {
            return;
        }
        try {
            await apiClient.patch(`/shopping-list/${listId}/items/${itemId}`, { scanned });
        } catch (error) {
            throw handleApiError(error);
        }
    },

    /**
     * Get the location of an arbitrary EAN for this shopping list.
     * Backend: GET /shopping-list/{shopping_list_id}/location-by-ean/{ean}
     * Returns { node_id: number }
     */
    getLocationByEan: async (
        listId: string | number,
        ean: string
    ): Promise<ProductLocationResponse | null> => {
        if (!isBackendEnabled() || !apiClient) {
            return null;
        }
        try {
            const response = await apiClient.get<ProductLocationResponse>(
                `/shopping-list/${listId}/location-by-ean/${encodeURIComponent(ean)}`
            );
            return response.data;
        } catch (error) {
            throw handleApiError(error);
        }
    },

    /**
     * Complete shopping list and submit feedback
     * Backend: POST /shopping-list/{shopping_list_id}/complete
     */
    completeShoppingList: async (
        listId: string,
        feedback?: { rating?: number; comment?: string; labels?: string[] }
    ): Promise<ShoppingListResponse> => {
        if (!isBackendEnabled() || !apiClient) {
            throw new Error('Backend required to complete shopping list');
        }
        const response = await apiClient.post<BackendShoppingList | ApiResponse<{ list: BackendShoppingList }>>(
            `/shopping-list/${listId}/complete`,
            feedback ?? {}
        );
        const raw = response.data as any;
        const backendList: BackendShoppingList = raw?.data?.list ?? raw;
        const list = transformBackendShoppingList(backendList);
        return { list };
    },

    /**
     * Calculate optimized shopping route
     * Backend: POST /shopping-list/{shopping_list_id}/calculate-route
     * Returns { distance, time } (e.g. distance in meters, time in minutes)
     */
    /**
     * Calculate route for a shopping list.
     * @param listId - Shopping list ID
     * @param currentPositionNodeId - Optional current node ID (e.g. after scan) to recalculate route from that position
     * @param includeSdm - Include SDM (sequential decision making) recommendations in the route. Default true. Set false when user scans the "next" item along the path.
     */
    calculateRoute: async (
        listId: string,
        currentPositionNodeId?: number,
        includeSdm: boolean = true
    ): Promise<CalculateRouteResponse> => {
        if (!isBackendEnabled() || !apiClient) {
            throw new Error('Backend required to calculate route');
        }
        const params: Record<string, number | boolean> = {
            include_sdm: includeSdm,
        };
        if (currentPositionNodeId != null) {
            params.current_position = currentPositionNodeId;
        }
        // Fire-and-forget audit event for route calculation.
        try {
            const sessionId = getAuditSessionId();
            const auditPayload = {
                event: 'calculate-route',
                payload: JSON.stringify({
                    include_sdm: includeSdm,
                    current_position: currentPositionNodeId ?? null,
                }),
                session_id: sessionId,
            };
            void apiClient.post(`/shopping-list/${listId}/audit`, auditPayload);
        } catch {
            // Swallow audit errors to avoid impacting main flow.
        }

        const response = await apiClient.post<CalculateRouteResponse>(
            `/shopping-list/${listId}/calculate-route`,
            undefined,
            {
                params,
                timeout: 60 * 60 * 1000, // 1 hour
            }
        );
        const data = response.data ?? {};

        // After we receive the route, audit the item ids and the node sequence.
        try {
            const detailed = await shoppingListApi.getShoppingListItemsDetailed(listId);
            const itemIds: number[] =
                detailed?.products?.flatMap((p) => p.item_ids || []) ?? [];

            const nodeIds: number[] =
                (data.route || [])
                    .map((step: any) => step?.nodeId)
                    .filter((id: unknown): id is number => typeof id === "number") ?? [];

            const routePath = nodeIds.length > 0 ? nodeIds.join("->") : "";

            void shoppingListApi.auditEvent(listId, "calculate-route-result", {
                item_ids: itemIds,
                route: routePath,
                include_sdm: includeSdm,
                current_position: currentPositionNodeId ?? null,
                distance: (data as any).distance ?? null,
                time: (data as any).time ?? null,
                recommended_product_ids: (data as any).recommended_product_ids ?? [],
            });
        } catch (error) {
            // Best-effort; log and continue.
            // eslint-disable-next-line no-console
            console.warn("[ShoppingList API] Failed to audit calculate-route result:", error);
        }

        return data;
    },

    /**
     * Generic audit endpoint for shopping list events.
     * POST /shopping-list/{shopping_list_id}/audit
     */
    auditEvent: async (
        listId: string | number,
        event: string,
        payload: unknown
    ): Promise<void> => {
        if (!isBackendEnabled() || !apiClient) {
            return;
        }
        try {
            const sessionId = getAuditSessionId();
            const body = {
                event,
                payload: typeof payload === 'string' ? payload : JSON.stringify(payload ?? {}),
                session_id: sessionId,
            };
            await apiClient.post(`/shopping-list/${listId}/audit`, body);
        } catch (error) {
            // Do not propagate audit failures to the UI.
            console.warn('[ShoppingList API] Failed to send audit event:', event, error);
        }
    },
};

